
import { mapGetters } from 'vuex'
import { EFilter } from '@/helpers/enums'

export default {
  name: 'SearchPanel',

  props: {
    mobile: Boolean,
    searchPools: Boolean,
    searchLocations: Boolean,
    searchWorkPreference: Boolean,
    searchHours: Boolean,
    searchDuration: Boolean,
    searchClosed: Boolean,
    totalResults: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      dialog: false,
      openFilterId: null,
      openFilterEnum: null,
      filterTitle: '',
      filterComponent: null,
      startScrollPosition: 0,
      backDropStyle: {
        width: '100%',
        opacity: '0'
      }
    }
  },

  computed: {
    ...mapGetters({
      categorizedPoolsList: 'filters/categorizedPoolsList',
      orphanPoolsList: 'filters/orphanPoolsList',
      preSelectCountry: 'preSelectCountry'
    }),

    filters() {
      return [
        {
          id: 'textSearchFilter',
          EFilter: EFilter.TEXT,
          component: () => import('@/components/app/filters/textSearch.vue'),
          condition: this.mobile || !this.mobile,
          title: this.$t('talent.project.search.text.title')
        },
        {
          id: 'locationsFilter',
          EFilter: EFilter.LOCATION,
          component: () => import('@/components/app/filters/location.vue'),
          condition: this.searchLocations,
          title: this.$t('talent.project.search.location.title')
        },
        {
          id: 'poolCategoriesFilter',
          EFilter: EFilter.POOLS,
          component: () => import('@/components/app/filters/poolCategories.vue'),
          condition: this.showPoolCategoriesFilter,
          title: this.$t('talent.project.search.pools.title')
        },

        {
          id: 'workPrefFilter',
          EFilter: EFilter.WORK_PREFS,
          component: () => import('@/components/app/filters/workPreference.vue'),
          condition: this.searchWorkPreference,
          title: this.$t('talent.project.search.work.preference.title')
        },
        {
          id: 'hoursFilter',
          EFilter: EFilter.HOURS,
          component: () => import('@/components/app/filters/hours.vue'),
          condition: this.searchHours,
          title: this.$t('talent.project.search.hours.title')
        },

        {
          id: 'durationFilter',
          EFilter: EFilter.DURATION,
          component: () => import('@/components/app/filters/duration.vue'),
          condition: this.searchDuration,
          title: this.$t('talent.project.search.duration.title')
        },

        {
          id: 'includeClosedFilter',
          EFilter: EFilter.OPEN_ONLY,
          component: () => import('@/components/app/filters/includeClosed.vue'),
          condition: this.searchClosed,
          title: this.$t('talent.project.search.include.closed.title')
        }
      ].filter((item) => item.condition)
    },

    firstRowFilters() {
      return this.filters.slice(0, 1)
    },

    secondRowFilters() {
      return this.filters.slice(1)
    },

    showPoolCategoriesFilter() {
      return this.searchPools && (this.categorizedPoolsList.length || this.orphanPoolsList.length)
    }
  },
  mounted() {
    document.addEventListener('keydown', this.trapFocus)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.trapFocus)
  },
  methods: {
    trapFocus(event) {
      const dialogRef = this.$refs.dialog
      if (!dialogRef) {
        return
      }
      const focusableElements = dialogRef.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements.length === 0) {
        console.error('No focusable elements found in dialog.')
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },
    openModal(filter) {
      if (this.openFilterId === filter.id) {
        this.closeModal()
        return
      }
      this.filterTitle = filter.title
      this.openFilterId = filter.id
      this.openFilterEnum = filter.EFilter
      this.filterComponent = filter.component
      // to see DOM is updated
      this.$nextTick(() => {
        this.dialog = true

        this.$nextTick(() => {
          const dialogRef = this.$refs.dialog
          if (dialogRef) {
            const focusableElements = dialogRef.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            if (focusableElements.length > 0) {
              focusableElements[0].focus() // Focus the first focusable element
            } else {
              dialogRef.focus() // Fallback to focusing the dialog itself
            }
          } else {
            console.error('Dialog reference not found.')
          }
        })
      })

      this.startScrollPosition = window.scrollY
      window.addEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      // current scroll position from window
      const checkScrollPosition = window.scrollY

      // when scroll exeeds
      if (
        checkScrollPosition - this.startScrollPosition > 150 ||
        checkScrollPosition - this.startScrollPosition < -150
      ) {
        this.onScrollThreshold()
      }
    },

    onScrollThreshold() {
      window.removeEventListener('scroll', this.handleScroll)
      this.closeModal()
    },
    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    closeModal() {
      this.dialog = false
      this.openFilterId = ''
      this.openFilterEnum = ''
      this.removeScrollListener()
    },

    handleClear() {
      // better to use nextTick to make sure the component is updated
      this.$nextTick(() => {
        if (this.$refs.filterComponent && this.$refs.filterComponent.clearFilter) {
          this.$refs.filterComponent.clearFilter()
        } else {
          console.error('Clear filter method is not available on the current component.')
        }
      })
    },

    async clearFilter(filter) {
      await this.$cleanQuery(this.$route.query, filter)
    },

    async cleanupFilters() {
      const loaderCheck = this.$route.query.any || this.$route.query.userLocation

      if (!loaderCheck) {
        this.openLoader()
      }
      await this.$router.push({ query: {} })
      this.dialog = false
      this.openFilterId = null
      this.openFilterEnum = null
      if (!loaderCheck) {
        this.closeLoader()
      }
    },

    openLoader() {
      this.$triggerLoader('ACTION', true)
    },

    closeLoader() {
      this.$triggerLoader('ACTION', false)
    },

    toggleFilter(filterId) {
      this.$set(this.openFilters, filterId, !this.openFilters[filterId])
    },

    activeFilterStyle(filter) {
      return this.openFilterId === filter.id || this.filterIsFilled(filter.EFilter)
        ? { outline: '1px solid', outlineColor: this.$vuetify.theme.currentTheme.primary }
        : {}
    },

    filterIsFilled(EFilter) {
      return this.$route.query[EFilter]
    },

    searchBtnHandler() {
      this.$refs.textSearch.triggerSearch()
    }
  }
}
